/**
 * The case Activity tab (Lloyd, 6 Oct 2026). Mirrors ActivityItem in
 * backend/src/utils/activity/caseActivity.ts; change one and change the other.
 */

export type ActivityChannel = 'portal' | 'email' | 'text' | 'call';
export type ActivityKind = 'note' | 'call' | 'email' | 'message';

export type NoteTopic = 'virtual_hearing' | 'court_note' | 'court_called' | 'client_called';

/** Twin of NOTE_TOPICS in backend/src/utils/activity/noteTopics.ts. */
export const NOTE_TOPIC_LABELS: Record<NoteTopic, string> = {
  virtual_hearing: 'Virtual hearing',
  court_note: 'Note from court',
  court_called: 'Court called',
  client_called: 'Client called',
};

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  at: string;
  direction: 'inbound' | 'outbound' | null;
  channels: ActivityChannel[];
  title: string | null;
  body: string;
  author: string | null;
  /**
   * `case`: filed on this citation. `client`: filed on no case — older notes and messages, and
   * inbound texts. Absent from an older API, which is treated as `case`.
   */
  scope?: 'case' | 'client';
  note?: {
    guid: string;
    topic: NoteTopic | null;
    source: 'manual' | 'dialpad' | 'clio' | 'system';
    ticketGuid: string | null;
    callOutcome: 'spoke' | 'voicemail' | 'no-answer' | null;
    callDurationSeconds: number | null;
    callRecordingUrl: string | null;
    /** The offer recorded from this note, if one was. Absent from an older API. */
    offerGuid?: string | null;
  };
  email?: {
    from: string | null;
    to: string[];
    mailbox: string;
    mailboxAddress: string | null;
    matchedBy: string | null;
    mailboxWarning: string | null;
    cdlRefusal: string | null;
  };
  message?: {
    source: 'manual' | 'system';
  };
}

// Parenthesised on purpose: webpack's DefinePlugin replaces `process.env` with an object literal,
// and an arrow body that then starts with `{` is read as a block, which broke the production build.
const host = () => (process.env.API_HOST || '');

async function send(path: string, method: string, body?: unknown): Promise<unknown> {
  const response = await fetch(`${host()}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || `Request failed (${response.status})`);
  }
  return response.status === 204 ? null : response.json().catch(() => null);
}

export default class CaseActivityApi {
  static async list(ticketGuid: string): Promise<ActivityItem[]> {
    return (await send(`/api/v1/manager/ticket/${ticketGuid}/activity`, 'GET')) as ActivityItem[];
  }

  static async createNote(input: {
    User_guid: string;
    Ticket_guid: string;
    UserNote_Title: string;
    UserNote_Desc: string;
    UserNote_Topic?: NoteTopic | null;
    UserNote_Kind?: 'note' | 'call';
    UserNote_CallDirection?: 'outbound' | 'inbound';
    UserNote_CallOutcome?: 'spoke' | 'voicemail' | 'no-answer';
  }): Promise<void> {
    await send('/api/v1/user-note', 'POST', input);
  }

  static async updateNote(input: {
    UserNote_guid: string;
    UserNote_Title?: string;
    UserNote_Desc?: string;
    UserNote_Topic?: NoteTopic | null;
    Ticket_guid?: string | null;
  }): Promise<void> {
    await send('/api/v1/user-note', 'PATCH', input);
  }

  static async deleteNote(guid: string): Promise<void> {
    await send(`/api/v1/user-note?UserNote_guid=${guid}`, 'DELETE');
  }
}
